import { supabase } from './supabase'

const BUCKET = 'pedidos'
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string
const EDGE_FN_URL = `${SUPABASE_URL}/functions/v1/generar-pdf`

// ─── Embed images as base64 ────────────────────────────────────────────────
// pdf.co no puede acceder a URLs de localhost ni relativas.
// Convertimos todas las imágenes a data URIs antes de enviar el HTML.

async function embedImages(html: string): Promise<string> {
  const imgRegex = /<img([^>]*)\ssrc="([^"]+)"([^>]*)>/gi
  const matches: { full: string; src: string }[] = []
  let match: RegExpExecArray | null
  while ((match = imgRegex.exec(html)) !== null) {
    matches.push({ full: match[0], src: match[2] })
  }

  for (const { full, src } of matches) {
    if (src.startsWith('data:')) continue
    try {
      const res = await fetch(src)
      const blob = await res.blob()
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = e => resolve(e.target?.result as string)
        reader.onerror = reject
        reader.readAsDataURL(blob)
      })
      html = html.replace(full, full.replace(`src="${src}"`, `src="${base64}"`))
    } catch {
      // Si no se puede obtener la imagen, se deja como está
    }
  }
  return html
}

// ─── PDF vía Edge Function → n8n → pdf.co ─────────────────────────────────
// Se manda el HTML directamente (no la URL del bucket) para que pdf.co
// lo renderice correctamente con todas sus imágenes en URLs absolutas.

async function generarPDF(
  pedidoId: string,
  origenNumero: string,
  html: string,
  tipo: 'reporte-cliente' | 'bitacora',
  urlVieja?: string,
): Promise<string> {
  const htmlConImagenes = await embedImages(html)
  const res = await fetch(EDGE_FN_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${ANON_KEY}`,
    },
    body: JSON.stringify({ html: htmlConImagenes, pedido_id: pedidoId, origen_numero: origenNumero, tipo, url_vieja: urlVieja ?? null }),
  })

  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.error || `Edge Function error: ${res.status}`)
  }

  const { url, error } = await res.json()
  if (error) throw new Error(error)
  return url
}

// ─── PDF de vista previa (no oficial) ──────────────────────────────────────
// Mismo pipeline (pdf.co), pero en carpeta separada y sin tocar cliente_link/
// bitacora_link. El llamador debe borrar el archivo con borrarArchivoStorage
// una vez que terminó de descargarlo, para no acumular PDFs en el bucket.

export async function generarPDFVistaPrevia(
  pedidoId: string,
  origenNumero: string,
  html: string,
  tipo: 'reporte-cliente' | 'bitacora',
): Promise<{ url: string; path: string }> {
  const htmlConImagenes = await embedImages(html)
  const res = await fetch(EDGE_FN_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${ANON_KEY}`,
    },
    body: JSON.stringify({ html: htmlConImagenes, pedido_id: pedidoId, origen_numero: origenNumero, tipo, preview: true }),
  })

  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.error || `Edge Function error: ${res.status}`)
  }

  const { url, path, error } = await res.json()
  if (error) throw new Error(error)
  return { url, path }
}

export async function borrarArchivoStorage(path: string): Promise<void> {
  await fetch(EDGE_FN_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${ANON_KEY}`,
    },
    body: JSON.stringify({ accion: 'borrar', path }),
  }).catch(() => {}) // best-effort: si falla la limpieza, no afecta al usuario
}

// Mismo parseo que extraerRutaBucket() en la Edge Function generar-pdf, para
// poder borrar del bucket una URL pública ya guardada en DB (p. ej.
// pedidos.cliente_link/bitacora_link) sin pasar por el flujo de generación.
export function extraerRutaBucket(url: string): string | null {
  try {
    const base = url.split('?')[0]
    const marker = `/object/public/${BUCKET}/`
    const idx = base.indexOf(marker)
    if (idx === -1) return null
    return base.substring(idx + marker.length)
  } catch {
    return null
  }
}

// Borra del bucket los informes (cliente/bitácora) de un pedido, si existen.
// Best-effort vía borrarArchivoStorage: si algún archivo ya no existe o la
// Edge Function falla, no se interrumpe el flujo que la llama.
export async function borrarInformesStorage(urls: (string | null | undefined)[]): Promise<void> {
  const paths = urls
    .filter((u): u is string => !!u)
    .map(extraerRutaBucket)
    .filter((p): p is string => !!p)
  await Promise.all(paths.map(borrarArchivoStorage))
}

// ─── Reportes ─────────────────────────────────────────────────────────────

export async function subirReporteCliente(pedidoId: string, origenNumero: string, html: string, urlVieja?: string): Promise<string> {
  return generarPDF(pedidoId, origenNumero, html, 'reporte-cliente', urlVieja)
}

export async function subirBitacora(pedidoId: string, origenNumero: string, html: string, urlVieja?: string): Promise<string> {
  return generarPDF(pedidoId, origenNumero, html, 'bitacora', urlVieja)
}

// ─── Imagen de prueba (sin actualizar noms.doc) ────────────────────────────

export async function subirImagenPrueba(
  file: File,
  pedidoId: string,
  origenNumero: string,
  rowId: string | number,
): Promise<string> {
  const safeName = file.name.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_.\-]/g, '')
  const path = `${origenNumero}-${pedidoId}/docs_soporte/${rowId}-${safeName}`

  const { error } = await supabase.storage
    .from(BUCKET)
    .upload(path, file, { upsert: true })
  if (error) throw new Error(error.message)

  const { data } = supabase.storage.from(BUCKET).getPublicUrl(path)
  return data.publicUrl
}

// ─── Evidencia de reapertura (Caso A, con trazabilidad) ────────────────────
// Se sube contra el pedido ORIGINAL (el pedido nuevo aún no existe en este
// punto del flujo — se crea recién al llamar a la RPC de reapertura).

export async function subirEvidenciaReapertura(
  file: File,
  origenNumero: string,
  pedidoId: string,
): Promise<string> {
  const safeName = file.name.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_.\-]/g, '')
  const path = `${origenNumero}-${pedidoId}/evidencia_reapertura/${Date.now()}-${safeName}`

  const { error } = await supabase.storage
    .from(BUCKET)
    .upload(path, file, { upsert: true })
  if (error) throw new Error(error.message)

  const { data } = supabase.storage.from(BUCKET).getPublicUrl(path)
  return data.publicUrl
}

// ─── Documentos de pruebas ─────────────────────────────────────────────────

export async function subirDocumentoPrueba(
  file: File,
  pedidoId: string,
  origenNumero: string,
  rowId: string | number,
  nomId?: string,
): Promise<string> {
  const safeName = file.name.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_.\-]/g, '')
  const path = `${origenNumero}-${pedidoId}/docs_soporte/${rowId}-${safeName}`

  const { error } = await supabase.storage
    .from(BUCKET)
    .upload(path, file, { upsert: true })
  if (error) throw new Error(error.message)

  const { data } = supabase.storage.from(BUCKET).getPublicUrl(path)

  // row_id_prueba es solo el PK dentro de la tabla prueba_XXXX correspondiente y NO es único
  // entre pruebas distintas (dos pruebas del mismo o de otro pedido pueden compartirlo). Sin
  // nomId no hay forma segura de identificar la fila de noms a actualizar.
  if (!nomId) {
    throw new Error('No se pudo identificar la prueba de forma única (falta nomId); no se guardó el documento.')
  }

  const { error: updateError } = await supabase
    .from('noms')
    .update({ doc: data.publicUrl })
    .eq('id', nomId)
  if (updateError) throw new Error(updateError.message)

  return data.publicUrl
}
